import { createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';

import type { StoredTask } from '@webapp/account/src/resources/tasks/lib';
import {
    type Task,
    TASK_STATUS_FAIL,
    TASK_STATUS_SUCCESS,
    TaskLocalStatus,
} from '@webapp/account/src/resources/tasks/task';

import { addWaiting, createTask, showOrders } from './async';
import type { TasksState } from './state';
import { initialState } from './state';

const updateWaitingTasksStatus = (state: TasksState): void => {
    const { tasks, waiting } = state;

    for (const uuid of Object.keys(waiting)) {
        const task = tasks[uuid];

        if (!task) {
            console.error(`task not found locally ${uuid}`, tasks);
            continue;
        }

        const success = TASK_STATUS_SUCCESS.includes(task.systemStatus);
        const fail = TASK_STATUS_FAIL.includes(task.systemStatus);

        if (success || fail) {
            delete waiting[uuid];

            let status: TaskLocalStatus;

            if (success) {
                status = TaskLocalStatus.COMPLETED;
            } else if (fail) {
                status = TaskLocalStatus.ERROR;
            } else {
                status = TaskLocalStatus.LOADING;
            }

            tasks[uuid].status = status;
        }
    }
};

export const tasksSlice = createSlice({
    name: 'tasks',
    initialState,
    reducers: {},
    extraReducers: (builder) =>
        builder
            // createTask
            .addCase(
                createTask.pending,
                (
                    state,
                    {
                        meta: {
                            arg: { uuid, taskType },
                        },
                    },
                ) => {
                    const created = dayjs().unix();
                    const status = TaskLocalStatus.LOADING;

                    state.tasks[uuid] = {
                        uuid,
                        created,
                        status,
                        type: taskType,
                        results: null,
                    };
                },
            )
            .addCase(createTask.fulfilled, (state, { payload }) => {
                const { tasks } = state;
                Object.assign(tasks[payload.uuid], payload);

                state.waiting[payload.uuid] = payload.id;
            })
            .addCase(createTask.rejected, ({ tasks, waiting }, { payload }) => {
                if (payload) {
                    // @ts-ignore
                    Object.assign(tasks[payload.uuid], payload);
                    // @ts-ignore
                    delete waiting[payload.uuid];
                }
            })
            .addCase(addWaiting.fulfilled, (state, { payload: { task, uuid } }) => {
                const { tasks, waiting } = state;

                if (!task) {
                    // сервер может вернуть пустой task — не роняем редьюсер (FRONT-2AA8)
                    return;
                }

                waiting[uuid] = task.id || task.taskId;
                tasks[uuid] = task;
            })
            .addCase(showOrders.fulfilled, (state, { payload: { tasks: newTasks } }) => {
                const taskHash: Record<number, StoredTask> = {};
                const { tasks, waiting } = state;

                const updatedTasks: { [key in string]: Task } = {};

                for (const newTask of newTasks) {
                    taskHash[newTask.id] = newTask;
                }

                for (const uuid of Object.keys(waiting)) {
                    const task = tasks[uuid];

                    if (taskHash[task.id]) {
                        updatedTasks[uuid] = {
                            ...tasks[uuid],
                            ...taskHash[task.id],
                        };
                    }
                }

                Object.assign(state.tasks, updatedTasks);
                updateWaitingTasksStatus(state);
            })
            .addCase(showOrders.rejected, (state) => {
                updateWaitingTasksStatus(state);
            }),
});
